/**
 * Dependency Mapper Executor
 *
 * Handles the 'dependency-mapper' and 'pipeline-dependencies' node execution.
 * Calls the ModernizeIT API Layer /dependencymapper endpoint.
 *
 * API Flow:
 * 1. POST /dependencymapper → returns job_id and results
 * 2. If status is 'running', poll GET /dependencymapper/{job_id}/status
 * 3. GET /dependencymapper/{job_id}/results → retrieve full results
 *
 * This node analyzes:
 * - COBOL dependencies (from ingested source)
 * - Java dependencies (from code_analysis generated code)
 *
 * Artifacts produced:
 * - static_analysis.json
 * - dependency_graph.json
 * - coupling_metrics.json
 * - risk_assessment.json
 * - microservice_boundaries.json
 * - impact_analysis.json
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the dependency mapper node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing dependency analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // Get source type from config (defaults to 'cobol')
  const sourceType = config.analysis_type || 'cobol'

  // 1. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    source_type: sourceType,
    // Node-level default action for missing symbols (propagated into missing_symbols.json pre_scan_defaults)
    missing_symbol_default_action: config.missing_symbol_default_action || 'compile-only-stub',
  }

  // 2026-01-27: Add job IDs for job-based S3 architecture
  if (sourceType === 'cobol') {
    // COBOL mode: requires ingest_job_id
    if (!context.jobs?.ingest_job_id) {
      throw new Error('ingest_job_id is required for COBOL dependency analysis. Run Upload node first.')
    }
    requestBody.ingest_job_id = context.jobs.ingest_job_id
  } else {
    // Java mode: requires code_analysis_job_id
    if (!context.jobs?.codeanalysis_job_id) {
      throw new Error('code_analysis_job_id is required for Java dependency analysis. Run Code Analysis node first.')
    }
    requestBody.code_analysis_job_id = context.jobs.codeanalysis_job_id
  }

  onProgress(5, `Submitting ${sourceType} dependency analysis job...`)

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/dependencymapper`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Dependency mapping failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Dependency analysis complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Processing dependency analysis...')
    const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        // Map polling progress from 15-95%
        const mappedProgress = 15 + (progress * 0.80)
        onProgress(mappedProgress, message)
      },
      signal
    })

    // Merge completed job data
    Object.assign(jobResponse, completedJob)
  }

  onProgress(100, 'Dependency analysis complete')

  // 5. Return formatted result with job data
  return formatResult(jobId, sourceType, jobResponse, context.jobs)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, sourceType, data, contextJobs = {}) {
  return {
    // Job tracking
    // 2026-01-27: Preserve upstream job IDs for downstream nodes
    jobs: {
      ...contextJobs,  // Preserve job chain (ingest_job_id, code_analysis_job_id, etc.)
      dependencymapper_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Source info
    source_type: sourceType,

    // Paths
    source_path: data.source_path,
    artifacts_path: data.artifacts_path,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Summary statistics
    summary: data.summary || {},

    // Artifacts produced
    artifacts: {
      json: data.json_artifacts || [],
      static_analysis: data.artifacts?.static_analysis,
      dependency_graph: data.artifacts?.dependency_graph,
      coupling_metrics: data.artifacts?.coupling_metrics,
      risk_assessment: data.artifacts?.risk_assessment,
      microservice_boundaries: data.artifacts?.microservice_boundaries,
      impact_analysis: data.artifacts?.impact_analysis,
    },

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
